import React from 'react';
import PropTypes from 'prop-types';
import GavelIcon from 'mdi-react/GavelIcon';
import ShieldAccountOutlineIcon from 'mdi-react/ShieldAccountOutlineIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import HomeVariantOutlineIcon from 'mdi-react/HomeVariantOutlineIcon';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import RobotIcon from 'mdi-react/RobotIcon';
import CashMultipleIcon from 'mdi-react/CashMultipleIcon';
import TruckDeliveryIcon from 'mdi-react/TruckDeliveryIcon';
import FileExportIcon from 'mdi-react/FileExportIcon';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import FeatureShowcase from '../../../shared/components/custom/FeatureShowcase';
import exampleCurrency from '../../user_tiers/components/exampleCurrency';
import AuctionItemPreview from './AuctionItemPreview';

/**
 * Auctions in two steps: set one up (items, bidding rules, who can bid),
 * then fans bid in the app and the club settles payment and delivery.
 */
const AuctionsShowcase = ({ collapsible, action }) => {
  const s = partial('shared');
  const currency = exampleCurrency(useConfig());
  return (
    <FeatureShowcase
      storageKey="auctionsShowcaseHidden"
      icon={GavelIcon}
      title={s('auctionInfoTitle')}
      subtitle={s('auctionShowcaseSubtitle')}
      showLabel={s('auctionShowcaseShow')}
      uses={[
        {
          key: 'setup',
          step: s('showcaseStep', { step: 1 }),
          icon: HomeVariantOutlineIcon,
          label: s('auctionSetupLabel'),
          text: s('auctionSetupText'),
          cards: [
            {
              key: 'items',
              icon: HomeVariantOutlineIcon,
              title: s('auctionCardItems'),
              text: s('auctionCardItemsText'),
            },
            {
              key: 'rules',
              icon: ClockOutlineIcon,
              title: s('auctionCardRules'),
              text: s('auctionCardRulesText'),
              points: [
                {
                  label: s('auctionRuleTimes'),
                  text: s('auctionRuleTimesText'),
                },
                {
                  label: s('auctionRuleRaise'),
                  text: s('auctionRuleRaiseText', { currency }),
                },
                {
                  label: s('auctionRuleExtension'),
                  text: s('auctionRuleExtensionText'),
                },
              ],
            },
            {
              key: 'access',
              icon: ShieldAccountOutlineIcon,
              title: s('auctionCardAccess'),
              text: s('auctionCardAccessText'),
              badges: ['Face ID'],
            },
            {
              key: 'promote',
              icon: BellRingOutlineIcon,
              title: s('auctionCardPromote'),
              text: s('auctionCardPromoteText'),
            },
          ],
        },
        {
          key: 'bid',
          step: s('showcaseStep', { step: 2 }),
          icon: GavelIcon,
          label: s('auctionBidLabel'),
          text: s('auctionBidText'),
          // An item in the app; bidding works
          interactive: true,
          itemsBeside: true,
          preview: <AuctionItemPreview />,
          items: [
            { key: 'auto', icon: RobotIcon, label: s('auctionItemAutoBid') },
            { key: 'pay', icon: CashMultipleIcon, label: s('auctionItemPay') },
            {
              key: 'delivery',
              icon: TruckDeliveryIcon,
              label: s('auctionItemDelivery'),
            },
            {
              key: 'export',
              icon: FileExportIcon,
              label: s('auctionItemExport'),
            },
          ],
        },
      ]}
      collapsible={collapsible}
      action={action}
    />
  );
};

AuctionsShowcase.propTypes = {
  collapsible: PropTypes.bool,
  action: PropTypes.node,
};

AuctionsShowcase.defaultProps = {
  collapsible: true,
  action: null,
};

export default AuctionsShowcase;
